/**
 * Job-system types and tuning constants.
 *
 * Deliberately free of Mongo and of every handler import, so the store (Mongo, no handlers) and the
 * conditions (pure, no Mongo) can both depend on this without a cycle.
 *
 * THE ROW IS DUMB ON PURPOSE. A job carries an intent (`type` + `payload`) and its execution
 * bookkeeping (attempts, lease, last error) — never a copy of the pipeline's state. Whether a job may
 * run right now is DERIVED from the exam on every evaluation (see `job-conditions.ts`). That is why
 * there is no 'blocked' status and no `dependsOn`: a teacher confirming the structure is not a write
 * to this collection, it is a write to the exam, and the next evaluation simply sees it. The exam
 * state stays the single source of truth; this collection is only the execution record.
 */

import type { Exam } from '../../exam.js';
import type { BatchIntent } from './batch-store.js';
import type { ProgressKey, SolutionArtifact } from '../../extraction-progress.js';
import type { StepCompletion, StepStart } from './step.js';

export const EXAM_JOBS_COLLECTION = 'exam_jobs';

// Every tuning number lives in ./limits.ts, grouped by what reads it. This file holds only the shape of a job.

export type JobStatus = 'pending' | 'claimed' | 'done' | 'failed' | 'cancelled';

/** Terminal statuses never run again. */
export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = ['done', 'failed', 'cancelled'];

/**
 * - `run`  — conditions hold, execute now.
 * - `wait` — a gate is closed but may open (structure not confirmed yet). Stays pending.
 * - `drop` — the reason to run is gone (no master solution uploaded any more, already extracted).
 *   Cancelled instead of waiting, because nothing will ever make it runnable.
 *
 * Collapsing `wait` and `drop` into a boolean is how jobs end up waiting forever.
 */
export type JobVerdict = 'run' | 'wait' | 'drop';

export interface ReadyResult {
  verdict: JobVerdict;
  /** Which condition decided it — persisted as `lastGate` so "why is nothing happening?" is a query. */
  because?: string;
}

/**
 * Whether a job type occupies the EVENT LOOP while it runs. Nothing else — it bounds no concurrency of its own.
 *
 * - `io` (default): the process is *idle* while the job awaits model/OCR calls. Throttling these on CPU would
 *   starve extraction merely because something else is busy, so they are not CPU-checked at all. Their real
 *   limits are the provider's concurrency (the model gate — note each such job fans out internally, so N jobs
 *   can mean far more than N concurrent calls) and this process's heap (load.ts).
 * - `heavy`: CPU-bound (PDF rasterisation, image conversion). Checked against event-loop utilisation, because
 *   it starves everything sharing the process, including the HTTP server.
 *
 * Memory deliberately does NOT follow this split: the biggest allocators are the `io` extraction jobs, so the
 * heap budget applies to every pool equally — one fixed budget per running job, see load.ts.
 */
export type JobPool = 'io' | 'heavy';

export type JobPayload = Record<string, unknown>;

export interface JobDoc<P extends JobPayload = JobPayload> {
  _id: string;
  examId: string;
  type: string;
  /**
   * Type-specific input, written once at enqueue and never modified afterwards. Also the ONLY part of the row a
   * readiness check may read: `ready(exam, job)` receives the job, so a gate that varies PER ROW is a branch on a
   * payload field rather than a new job type — which is why rows carry no `requires`/`dropIf` of their own
   * (job-conditions.ts records why that indirection was removed).
   *
   * PARAMETERS, NOT INSTRUCTIONS. A payload selects among the checks its type already implements
   * (`{ requireStructure: true }` → `if (job.payload.requireStructure && !structureConfirmed(exam)) return 'wait'`).
   * It cannot NAME a condition the code has never heard of: resolving that would need a string→function registry,
   * which is the machinery this design deleted. So the menu is defined in typechecked code; the row only picks
   * from it.
   *
   * A payload field that changes the gate is usually also a `dedupeKey` field — see there — and usually NOT a
   * `lockKey` field, since two gated variants of one job normally still touch the same documents.
   */
  payload: P;
  status: JobStatus;
  /**
   * Mirrors `status ∈ {pending, claimed}`. Exists solely to key the unique dedupe index with an equality
   * match; never read as truth — `status` is. Maintained by the store's single transition writer.
   */
  live: boolean;
  /**
   * The resource this job needs exclusively while it runs. At most one CLAIMED job per key, across all
   * instances — that is the whole locking design (see the unique partial index in job-store).
   *
   * Granularity is a per-type choice, and it is the knob for parallelism: everything that rewrites the
   * task tree shares `exam:<id>:tasks` and therefore serialises, while work on its own documents
   * (`submission:<id>`) runs alongside it. Defaults to `exam:<id>` — the safe, fully serial key.
   * "May this run now?"
   */
  lockKey: string;
  /** Not before this instant: retry backoff, wait backoff, or a deliberate debounce. */
  runAfter: Date;
  /**
   * Unique while the job is live (pending/claimed) — the replacement for ad-hoc "already running?" guards.
   * "Should this row exist?"
   *
   * Defaults to `<type>:<examId>`, which IGNORES the payload. Two rows of one type whose payloads gate them
   * differently therefore collide, and the second `enqueueJob` returns `null` — deduped, silently, and correctly by
   * the key's own rules. So a payload variant that is genuinely separate work has to appear in the key
   * (`<type>:<examId>:<mode>`); a variant that is merely the same work re-requested must not.
   *
   * Get this wrong and work is lost without a trace, whereas an over-broad `lockKey` only makes things slow. That
   * asymmetry is why the two keys are chosen independently rather than derived from one another.
   */
  dedupeKey: string;
  /**
   * Which RUN this row belonged to — the set an announcement is aggregated over. Written by the dispatch, never by
   * a call site (see `openOrJoinBatch`), and never modified afterwards.
   *
   * "What happened", as opposed to `dedupeKey`'s "should this row exist" and `lockKey`'s "may this run now". It is
   * what lets the last row of a wave read what the whole wave did instead of reading it off exam state — and exam
   * state was the wrong place for it, because it holds one current value while a run is a set of past events.
   *
   * Optional only for rows written before batching existed; every new row has one.
   */
  batchKey?: string;
  /**
   * WHAT THIS ROW DID, as opposed to what its `payload` asked for. Written once, by the terminal write, from the
   * handler's return value.
   *
   * The two are genuinely different and an announcement needs both: a row that was asked to grade eight
   * submissions and graded six is not the same fact as either number alone, and a row cancelled before running was
   * asked for something and did nothing at all. `note` cannot carry this — it is one diagnostic string by contract
   * — so anything an announcement has to name in DATA goes here.
   *
   * Deliberately untyped at this layer: the store must not know what a step announces. The step's own declaration
   * writes and reads it, so its shape is that type's business (see `JobHandlerResult.result`).
   */
  result?: Record<string, unknown> | null;
  /**
   * The handler reported there was nothing to do. A successful outcome, and NOT the same as having done the work —
   * which is why it is on the row and not only in the transition's reason: an announcement aggregated over a wave
   * must not name units that were skipped.
   */
  skipped?: boolean;
  /**
   * WHY THIS ROW EXISTS — one entry per trigger that asked for it, in its step's own vocabulary.
   *
   * Normally one, and more than one exactly when a later trigger DEDUPED onto this row: an upload replacing a
   * master solution and a grading scheme, both landing on the same (task, submission), is one row asked for twice.
   * The joining trigger's cause is pushed here, because a dedupe produces no row of its own and its reason would
   * otherwise vanish — and a wave that heard only the first cause tells the teacher about one file while silently
   * having used two.
   *
   * SEPARATE FROM `payload`, which is written once and never modified: `ready()` reads the payload and `dedupeKey`
   * is computed from it, so a payload that grows is a row whose identity and gating change underneath it. This
   * carries no scope for the same reason it can afford to grow — the scope is the payload, the outcome is `result`,
   * and this is read only by an announcement.
   */
  intents?: BatchIntent[];
  /**
   * How long this PARTICULAR job may run, overriding its type's default.
   *
   * For work whose cost is known at enqueue time and varies by orders of magnitude between rows — grading an exam
   * with 8 submissions against one with 400 — where a single per-type number has to be the worst case and then
   * every fast row inherits the worst case's crash-recovery latency. Resolved through `resolveJobRuntimeMs`, which
   * validates and clamps it, so a bad value degrades to the default rather than creating an immortal claim.
   */
  maxRuntimeMs?: number;
  attempts: number;
  maxAttempts: number;
  claimedBy?: string | null;
  /** When the claim was taken. Immutable for the life of the claim — see `ClaimGuard`. */
  claimedAt?: Date | null;
  /**
   * When the worker holding this job will give up on it, written at claim time as `claimedAt + resolved runtime`.
   *
   * **Stored rather than recomputed, because the sweeper must not need the registry to do its job.** It is the one
   * reader that has to reason about jobs whose type it may not have registered — another instance's types during a
   * rolling deploy — and asking "which rows are past their deadline?" has to stay a single indexed range query
   * rather than a full scan of claimed rows plus a per-row lookup. The row carries its own answer.
   */
  deadlineAt?: Date | null;
  /** Condition that last returned `wait`/`drop`. Diagnostics only — never an input to a decision. */
  lastGate?: string | null;
  /** Handler's own note about the outcome, e.g. what it skipped and why. */
  note?: string | null;
  lastError?: string | null;
  /**
   * First time this job was found not-runnable. Cleared once it runs.
   *
   * DIAGNOSTIC ONLY — nothing branches on it. It used to feed a `maxWaitMs` cancellation, which was removed with
   * `reconcile.ts` because nothing ever set `maxWaitMs` and the branch was therefore unreachable. Kept because it
   * answers the question a human debugging a parked exam actually has: "has this been waiting five minutes or
   * three days?", which neither `createdAt` (a job can wait after a requeue) nor `lastGate` can say.
   */
  firstWaitAt?: Date | null;
  createdAt: Date;
  finishedAt?: Date | null;
}

export interface JobHandlerResult {
  /** The handler decided there was nothing to do. Completes the job; not a failure. */
  skipped?: boolean;
  /** Short note for the row, e.g. what was skipped and why. */
  note?: string;
  /**
   * WHAT THIS ROW DID, for the wave's announcement to aggregate — persisted to `JobDoc.result`.
   *
   * Return it when the handler learns something the announcement needs and nothing else can re-derive: which
   * submissions it actually graded, which tasks its write actually changed. A handler that returns nothing here is
   * saying its payload already describes its outcome, which is true for most types.
   *
   * Keep it SMALL and LOCAL — what this row did, not what the wave asked for. Four hundred rows each carrying the
   * whole cohort is real duplication, and the aggregate does not need it: the batch has every row.
   */
  result?: Record<string, unknown>;
}

export interface JobContext<P extends JobPayload = JobPayload> {
  job: JobDoc<P>;
  /** The exam the readiness check ran against. A snapshot — re-read it if the handler needs fresh data. */
  exam: Exam;
  /**
   * Aborted once the job has run for its resolved budget — `deadlineAt` on the row. Pass it to anything that
   * accepts a signal.
   *
   * The runner stops waiting at that point whether or not the handler cooperates, and writes the job's outcome —
   * so this is how a handler AVOIDS becoming an orphan that keeps working, and keeps writing, on a job the queue
   * has already handed to someone else. A handler that ignores it is not incorrect (handlers are idempotent by
   * contract) but it does waste the work.
   */
  signal: AbortSignal;
}

/** Fired after every status change, including requeues. One hook for all of them by design. */
export interface JobTransition<P extends JobPayload = JobPayload> {
  job: JobDoc<P>;
  from: JobStatus;
  to: JobStatus;
  /** Machine-readable cause: 'claimed' | 'completed' | 'skipped' | 'handler-error' | 'lease-expired' | … */
  reason: string;
  error?: string;
}

export interface JobDefinition<P extends JobPayload = JobPayload> {
  type: string;

  /**
   * May this job run against this exam? Pure and total: no I/O, no throwing. Gets the whole exam doc
   * (which carries `tasks`), so task-tree checks stay pure; anything needing other collections belongs
   * in the handler as a skip, the way the pipeline steps already do it.
   *
   * Omitted means "always runnable". This is the ONLY place readiness is decided — there is no row-level
   * gate mechanism, because a gate is a property of the type and this function already receives the job, so
   * anything a row could have carried it can express by branching on `job.payload`.
   */
  /**
   * MAY BE ASYNC, for the one gate that is a fact about the QUEUE rather than about the exam.
   *
   * Everything gated on exam state stays synchronous and should — a gate that reads the exam has the exam. What
   * needed the escape hatch is ordering WITHIN a step: `gradingConsistency` compares one task's grades across
   * submissions, so it may only run once no `grading` row for that task is left, and no amount of exam state says
   * that. Expressing it as a readiness gate rather than as a follow-up is what keeps the whole wave in ONE batch and
   * therefore one announcement — a follow-up enqueued after the step drained would reopen the step and announce twice.
   *
   * Evaluated BEFORE the claim and once per pending row per tick, so an async gate must stay a single indexed query.
   */
  ready?(exam: Exam, job: JobDoc<P>): JobVerdict | ReadyResult | Promise<JobVerdict | ReadyResult>;

  handler(examId: string, payload: P, context: JobContext<P>): Promise<JobHandlerResult | void>;

  /** Defaults to `<type>:<examId>` — one live job of this type per exam. */
  dedupeKey?(examId: string, payload: P): string;

  /**
   * How long a row keeps ABSORBING equivalent enqueues — until it goes terminal (the default), or only until it is
   * CLAIMED.
   *
   * `'terminal'` is right whenever the row's work is fully described by its payload AND by inputs that cannot change
   * under it. A second request for the same work is then the same work, so joining the row already doing it is
   * correct however far along it is.
   *
   * `'claimed'` is for a type whose OUTPUT DEPENDS ON STATE IT READS WHEN IT RUNS. For those, joining a running row
   * is not coalescing — it is silent loss, because the work the joiner asked for is not the work the running row is
   * doing. `grading` is the case, and the reason is worth stating precisely because it changed: it used to be that a
   * row's SCOPE was resolved late (which submissions to grade). That is gone — a row names one submission. What
   * remains is that a grade is computed against the task's master solution and grading scheme, read from the tree at
   * RUN time and not carried in the payload. A grading-scheme upload landing while a row for that task is running
   * asks for a re-grade against the new scheme; deduping it onto the running row means the row finishes against the
   * OLD scheme, the ask is silently dropped, and the wave announces the new scheme as applied.
   *
   * So the two mechanisms divide by the row's status, and between them they cover both cases:
   *  - PENDING twin  → the enqueue dedupes and pushes its cause onto that row (`JobDoc.intents`). The row has read
   *    nothing yet, so it will see both changes: one row, both causes, announcement true.
   *  - CLAIMED twin  → the row no longer holds the dedupe key, so the enqueue INSERTS. The running row finishes and
   *    reports its own cause; the new row re-grades against the new inputs and reports the new cause. `lockKey`
   *    serialises the two, so the later one genuinely reads the later state.
   *
   * WHAT IT COSTS is bounded, and worth stating: a second row for the same key can exist while the first runs. It
   * cannot become a third, because the second is `pending` and absorbs everything after it.
   *
   * Implemented as the meaning of the `live` flag — see `enqueueJob` and `claimJob`. A row released at the claim
   * that later returns to `pending` (a retry, an expired lease) tries to become live again and is CANCELLED if an
   * equivalent row is live by then, which is the same dedupe answer arrived at one step later (`supersede`).
   */
  dedupeUntil?: 'terminal' | 'claimed';

  /**
   * What this type needs exclusive access to while it runs. Defaults to `exam:<examId>`, which
   * serialises everything for that exam; narrow it to run alongside unrelated work:
   *   `exam:<id>:tasks`      — rewrites the task tree (structure, master solution, grading scheme)
   *   `submission:<id>`      — touches only that submission (answer extraction, grading)
   */
  lockKey?(examId: string, payload: P): string;

  // NO RUN OR BATCH CONCEPT HERE AT ALL, and that is the point of the current design. A definition used to
  // derive its run from the exam, then from a key its dispatcher minted and threaded through every enqueue.
  // Completion is now a question a row asks about the QUEUE at its own terminal transition — "is any job of my
  // step still live for this exam?" (`anyLiveJobForTypes`) — so a new type needs a `type`, a `handler`, and
  // nothing else to take part in progress reporting.

  maxAttempts?: number;

  /**
   * How long this type may run before its own worker gives up on it. Defaults to `MAX_JOB_RUNTIME_MS`.
   *
   * **Set this per type rather than leaving one global number**, because the global has to be the slowest job in the
   * system and that cost is paid by every other type: after an ungraceful death, a claimed row is only reclaimable
   * at `deadline + LEASE_GRACE_MS`, so a 2-minute OCR job that died in a SIGKILL sits unreclaimable for as long as a
   * full grading run would have been allowed. Declaring the real budget per type makes crash recovery proportional
   * to the work rather than to the worst case.
   *
   * A row may override it via `maxRuntimeMs` when the cost varies within the type; see `resolveJobRuntimeMs` for the
   * precedence and the clamp.
   */
  maxRuntimeMs?: number;

  /** Whether this type occupies the event loop. Defaults to `io`; use `heavy` for CPU-bound work. */
  pool?: JobPool;

  /**
   * Behaviour on status changes. One coarse hook — branch on `to`/`reason` inside it:
   *   onTransition: e => { if (e.to === 'failed') … }
   * Hook failures are logged and swallowed: a hook must never take the runner or the job down.
   */
  onTransition?(event: JobTransition<P>): void | Promise<void>;

  /**
   * What happens when the exam has no live row left of this job's STEP — declared, not wired.
   *
   * The step is derived (`stepTypesOf`), the "am I last?" question is asked once in `job-hooks.ts`, and everything
   * declared here runs exactly once per drained wave. This replaced an `onTransition: setupStepSync(closure)` per
   * type: the wrapper had to be applied identically at seven call sites, each closure restated its own step name,
   * and a type could silently be given the wrong one or none. See `StepCompletion`.
   *
   * `onTransition` is still the place for work that belongs to ONE ROW rather than to the step — `gsRefine`
   * dispatches its own re-grade there, and declares this as well.
   */
  /**
   * What a dispatch does before this type's rows exist — see `StepStart`. The mirror of `onStepComplete`: one owns the
   * moment a step opens, the other the moment it drains, and neither names the step.
   */
  onStepStart?: StepStart;

  onStepComplete?: StepCompletion;

  /**
   * How MANY rows one dispatch of this type creates, and what opens before them — the other half of the pair with
   * `onStepComplete`. A type that is always a single row from a caller's own payload declares nothing.
   *
   * DECLARED, NOT WRITTEN. Every dispatcher in this system was the same four steps in the same order: open the
   * step's progress run, enumerate the units, enqueue one row each, and resolve the twin when an enqueue deduped.
   * Written out seven times, the order was the part that kept being got wrong — open the record AFTER the rows and
   * the step announces itself against the previous pass's total. `dispatchJobs` runs those steps; a type says only
   * what its units are.
   *
   * See `BatchSpec`, and `dispatch.ts` for the engine.
   */
  batch?: BatchSpec<P>;
}

/**
 * What one dispatch of a type turns into: how many rows, and what they carry. When the step OPENS is `onStepStart`.
 *
 * `scope` is whatever the caller passed to `dispatchJobs` — the engine never inspects it, so it is typed where it is
 * read (`batch.units`) rather than here, which is also what keeps a dispatch site from importing a job module and its
 * agents with it.
 */
export interface BatchSpec<P extends JobPayload = JobPayload> {
  /**
   * The rows to create, one payload each. THE ONE PIECE THAT CANNOT BE SHARED, and the reason this is a function
   * rather than a field: a task-tree walk, a submission list, and refs grouped by main task have nothing in common
   * but their shape at the end.
   *
   * Return one payload for a type that is one row per dispatch. Returning none is normal and not an error — a
   * generation with nothing left to generate enqueues nothing.
   */
  units: (examId: string, scope: DispatchScope) => Promise<P[]> | P[];
  /**
   * Resolve a deduped enqueue to the live row already doing that work, instead of reporting nothing.
   *
   * For the types a caller WAITS on: clicking retry twice has to report on the first click's job. Off by default,
   * because the lookup is a second query and a fire-and-forget dispatch has nothing to do with the answer.
   */
  joinTwin?: boolean;
  /**
   * How long `dispatchJobsAndWait` / `waitForJobs` will wait for these rows before reporting them as still running.
   *
   * A PROPERTY OF THE TYPE, not of the caller, which is why it lives here: what a wait should be is set by how long
   * this work takes and by who is on the other end of it, and both are facts about the type. It used to be a constant
   * in each dispatcher module, which meant a caller could pass a different one and none of them said why the number
   * was what it was.
   *
   * Timing out costs the caller its knowledge of the outcome, never the work: the rows stay queued, keep their
   * remaining attempts, and their step still announces itself when the last of them lands.
   */
  waitMs?: number;
}

/**
 * A dispatch's caller-supplied scope.
 *
 * `object` rather than `Record<string, unknown>`, and the looseness is the honest type: the engine never reads a field
 * of it, it only hands it to `units`. A record would additionally demand an index signature from every caller's own
 * scope interface (`GradingScope`, `RefineRequest`), which would mean either polluting those with `[key: string]:
 * unknown` — losing the typo-checking that is the point of having them — or spreading them into a literal at every
 * call site to get an implicit one. The reading end casts once, where the shape is actually known.
 */
export type DispatchScope = object;

export const isTerminalJobStatus = (status: JobStatus): boolean => TERMINAL_JOB_STATUSES.includes(status);

/** The safe default: everything for one exam serialises. A type opts into parallelism by narrowing it. */
export const defaultLockKey = (examId: string): string => `exam:${examId}`;

/** Everything that rewrites `exam.tasks` must share this key, or two of them lose each other's writes. */
export const taskTreeLockKey = (examId: string): string => `exam:${examId}:tasks`;

/**
 * A setup step, as the progress records name it.
 *
 * An alias rather than its own union: a step the queue knows about that the state does not (or the reverse) is
 * not a thing that can exist. Type-only, so job.ts stays free of the entity module at runtime.
 */
export type SetupStep = ProgressKey;

/**
 * The job types that make up each setup step — THE definition of "am I the last one?" for every step's completion.
 *
 * ALL PRODUCERS OF ONE ARTIFACT ARE ONE STEP, and this is the one place where getting the set wrong is silent:
 * a step asking only about its OWN type would announce the master solution finished while a generation for
 * another root is still queued, or while a refine that arrived mid-extraction is still waiting. A new job type
 * belongs in here as well as in `types/index.ts`, and it is exhaustive over `SetupStep` by type so the compiler
 * catches a step that was forgotten entirely.
 *
 * This replaced a per-run `batchKey`. The key was minted by asking this same question — "are any rows of these
 * types live? then join theirs" — so it was a label on a set already defined by the query; see
 * `anyLiveJobForTypes` for what removing it bought.
 *
 * Still NOT the same question as `anySetupStepOngoing`: that also reports the split confirmation, which waits on
 * the TEACHER and has no job. Jobs answer "is machine work outstanding"; exam state answers "is a human gate
 * open". Both are needed.
 */
export const STEP_JOB_TYPES: Record<SetupStep, readonly string[]> = {
  task: ['structure', 'structureAnnounce'],
  answer: ['answerExtraction'],
  masterSolution: ['msExtraction', 'msGeneration', 'msRefine'],
  gradingScheme: ['gsExtraction', 'gsGeneration', 'gsRefine'],
};

/**
 * The job type that reads each uploaded artifact, fills its gaps, and corrects it.
 *
 * Here rather than at the call sites because `SolutionArtifact` is how the rest of the pipeline names these two
 * things — a confirmation, an upload handler and a refine tool all hold an artifact, not a job type — and a
 * hand-written `artifact === 'masterSolution' ? 'msRefine' : 'gsRefine'` at each of them is one more place for the
 * pair to drift. `STEP_JOB_TYPES` above is the same idea for steps.
 */
export const EXTRACTION_JOB_TYPE: Record<SolutionArtifact, string> = {
  masterSolution: 'msExtraction',
  gradingScheme: 'gsExtraction',
};

export const GENERATION_JOB_TYPE: Record<SolutionArtifact, string> = {
  masterSolution: 'msGeneration',
  gradingScheme: 'gsGeneration',
};

export const REFINE_JOB_TYPE: Record<SolutionArtifact, string> = {
  masterSolution: 'msRefine',
  gradingScheme: 'gsRefine',
};

/**
 * STEPS THAT ARE NOT SETUP STEPS — the same "these types share one wave" grouping, for a step that keeps no
 * progress record on the exam.
 *
 * `grading` is the only one, and it needs the grouping for the same reason `masterSolution` does: its wave has three
 * PHASES, not one. A submission's task is graded, then that task's grades are reconciled across submissions
 * (`gradingConsistency`), then multi-task grammar groups are graded (`languageUseGroupGrading`). All three write grades,
 * so the wave is not over until all three are, and announcing after the first would report grades that the next two
 * are about to change.
 *
 * Deliberately separate from `STEP_JOB_TYPES` rather than folded into it: that map is keyed by `SetupStep`, and a
 * `SetupStep` is a thing with a progress record, a status and an announcement delta. Grading has none of those — its
 * progress is a session refcount and a unit set — so adding it there would give it four fields it must not have.
 */
export const EXTRA_STEP_TYPES: Record<string, readonly string[]> = {
  grading: ['grading', 'gradingConsistency', 'languageUseGroupGrading'],
};

/** `STEP_JOB_TYPES` read the other way. Built once, because the completion hook asks per terminal transition. */
const SETUP_STEP_OF_TYPE: Record<string, SetupStep> = Object.fromEntries(
  Object.entries(STEP_JOB_TYPES).flatMap(([step, types]) => types.map(type => [type, step as SetupStep])),
);

/** Both maps read the other way, so a type resolves to its step whether or not that step is a setup step. */
const STEP_OF_TYPE: Record<string, string> = {
  ...SETUP_STEP_OF_TYPE,
  ...Object.fromEntries(Object.entries(EXTRA_STEP_TYPES).flatMap(([step, types]) => types.map(type => [type, step]))),
};

/** Which setup step this job type belongs to, or undefined for a type that is not part of one. */
export const setupStepOf = (type: string): SetupStep | undefined => SETUP_STEP_OF_TYPE[type];

/**
 * What a batch batches: the STEP, or the type itself where there is no step.
 *
 * The same scope `STEP_JOB_TYPES` defines for completion, and it has to be — a batch is open exactly while its step
 * has live rows, so a batch scoped any finer would close while the step was still running and announce a fragment of
 * the wave. `masterSolution`'s three producing types share one announcement, so they share one batch; `grading` has no
 * setup step and batches under its own name.
 */
export const batchScopeOf = (type: string): string => STEP_OF_TYPE[type] ?? type;

/**
 * The types whose rows share this type's step — the set "is the step drained?" asks about.
 *
 * A TYPE IN NO SETUP STEP IS ITS OWN STEP, which is what lets `grading` use the same hook as the four setup steps:
 * its step is `['grading']`, so "no live row of my step" is exactly the predicate its old bespoke `onTransition`
 * spelled out by hand. Nothing has to be added here for a new type; belonging to a step is `STEP_JOB_TYPES`, and not
 * belonging to one needs no entry at all.
 */
export const stepTypesOf = (type: string): readonly string[] => {
  const step = STEP_OF_TYPE[type];
  if (!step) return [type];
  return STEP_JOB_TYPES[step as SetupStep] ?? EXTRA_STEP_TYPES[step] ?? [type];
};

/**
 * Work confined to one submission document — runs alongside anything task-tree-bound.
 *
 * THE WHOLE DOCUMENT, so this excludes every other holder of it. Right for answer extraction, which replaces the
 * entire `taskSubmissions` array; too coarse for work that writes one addressable element of it, which is what
 * the two narrower keys below are for.
 */
export const submissionLockKey = (submissionId: string): string => `submission:${submissionId}`;

/**
 * ONE MAIN TASK of one submission — the element a grading row writes with `arrayFilters`.
 *
 * Narrower than `submissionLockKey` so a submission's tasks grade CONCURRENTLY instead of one after another. That
 * is safe because the writes are disjoint and addressed, not read-modify-write: each row `$set`s inside its own
 * `taskSubmissions.$[main]` element (see `gradingAgent`'s grade write and `prepareRegradingRun`).
 *
 * THE INVARIANT A LOCK KEY MUST SATISFY: its partition has to be COARSER THAN OR EQUAL TO the type's
 * `dedupeKey` partition. Two rows that could dedupe onto each other must land on the same lock, or the later one
 * would not be serialised behind the earlier one and would read state the earlier one had not written yet.
 * `grading`'s dedupe key is `grading:<exam>:<taskKey>:<submission>:<scope>` — strictly finer than this — so
 * twins still serialise and `dedupeUntil: 'claimed'` keeps meaning what it says.
 */
export const submissionTaskLockKey = (submissionId: string, taskKey: string): string =>
  `submission:${submissionId}:task:${taskKey}`;

/**
 * ONE LanguageUseScheme's result for one submission — the `languageUseRuleResults` element a group row upserts.
 *
 * A top-level array, disjoint from `taskSubmissions`, so a group grades alongside the content grading of its own
 * member tasks rather than behind all of them. Same invariant as above: `languageUseGroupGrading`'s dedupe key
 * adds only the exam id and the settle flag, so twins still share this lock.
 */
export const submissionLanguageUseLockKey = (submissionId: string, schemeId: string): string =>
  `submission:${submissionId}:languageUse:${schemeId}`;

/**
 * The exam's UPLOAD, held by the split. Deliberately not `taskTreeLockKey`: the split writes Submission
 * documents and the upload progress, never `tasks`, so it must be able to run beside a structure extraction —
 * which on a first upload it does. Exclusive per exam all the same, because two concurrent splits of one
 * upload would each create the whole cohort.
 */
export const uploadLockKey = (examId: string): string => `exam:${examId}:upload`;
